import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import axios from '../utils/api';

const formatDate = (value) => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getPresetRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === 'today') return { startDate: formatDate(today), endDate: formatDate(today) };
  if (preset === 'week') {
    const start = new Date(today);
    start.setDate(start.getDate() - start.getDay());
    return { startDate: formatDate(start), endDate: formatDate(today) };
  }
  if (preset === 'month') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { startDate: formatDate(start), endDate: formatDate(today) };
  }
  if (preset === 'year') {
    const start = new Date(today.getFullYear(), 0, 1);
    return { startDate: formatDate(start), endDate: formatDate(today) };
  }
  return { startDate: formatDate(today), endDate: formatDate(today) };
};

const ProductViewsDashboard = () => {
  const initialRange = getPresetRange('month');
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [selectedPreset, setSelectedPreset] = useState('month');

  const [filterUserId, setFilterUserId] = useState('');
  const [filterProductId, setFilterProductId] = useState('');

  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load filter dropdown options once
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [usersRes, productsRes] = await Promise.all([
          axios.get('/api/product-views/filter-users'),
          axios.get('/api/product-views/filter-products'),
        ]);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      } catch {
        // non-fatal — filters will be empty
      } finally {
        setLoadingFilters(false);
      }
    };
    loadFilters();
  }, []);

  const loadAnalytics = useCallback(async (start, end, userId, productId) => {
    try {
      setLoading(true);
      setError('');
      const params = { start_date: start, end_date: end };
      if (userId) params.user_id = userId;
      if (productId) params.product_id = productId;
      const res = await axios.get('/api/product-views/analytics', { params });
      setAnalytics(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load product view analytics');
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadAnalytics(initialRange.startDate, initialRange.endDate, '', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (preset) => {
    const range = getPresetRange(preset);
    setSelectedPreset(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    loadAnalytics(range.startDate, range.endDate, filterUserId, filterProductId);
  };

  const handleApply = (e) => {
    e.preventDefault();
    setSelectedPreset('custom');
    loadAnalytics(startDate, endDate, filterUserId, filterProductId);
  };

  const rows = analytics?.rows || [];

  return (
    <Container fluid className="py-4">
      <Row className="mb-3">
        <Col>
          <h2 className="h4 mb-1">Product Views Dashboard</h2>
          <p className="text-muted mb-0">
            Analyze which products buyers viewed, by user, date range, and product.
          </p>
        </Col>
      </Row>

      {/* Summary Cards */}
      {analytics && (
        <Row className="g-3 mb-4">
          <Col xs={6} md={4}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div className="fs-3 fw-bold text-primary">{analytics.total_views}</div>
                <div className="text-muted small">Total Views</div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={4}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div className="fs-3 fw-bold text-success">{analytics.unique_users}</div>
                <div className="text-muted small">Unique Buyers</div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={4}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div className="fs-3 fw-bold text-info">{analytics.unique_products}</div>
                <div className="text-muted small">Unique Products</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <Form onSubmit={handleApply}>
            <Row className="g-3 align-items-end">
              {/* Presets */}
              <Col xs={12}>
                <div className="d-flex flex-wrap gap-3">
                  {['today', 'week', 'month', 'year'].map((preset) => (
                    <Form.Check
                      key={preset}
                      type="checkbox"
                      id={`preset-${preset}`}
                      label={preset === 'today' ? 'Today' : preset === 'week' ? 'This Week' : preset === 'month' ? 'This Month' : 'This Year'}
                      checked={selectedPreset === preset}
                      onChange={() => applyPreset(preset)}
                    />
                  ))}
                </div>
              </Col>

              {/* Date Range */}
              <Col xs={6} md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setSelectedPreset('custom'); }}
                  />
                </Form.Group>
              </Col>
              <Col xs={6} md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setSelectedPreset('custom'); }}
                  />
                </Form.Group>
              </Col>

              {/* User Filter */}
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Filter by User</Form.Label>
                  <Form.Select
                    value={filterUserId}
                    onChange={(e) => setFilterUserId(e.target.value)}
                    disabled={loadingFilters}
                  >
                    <option value="">All Users</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Product Filter */}
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Filter by Product</Form.Label>
                  <Form.Select
                    value={filterProductId}
                    onChange={(e) => setFilterProductId(e.target.value)}
                    disabled={loadingFilters}
                  >
                    <option value="">All Products</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col xs={12} md="auto">
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                  Apply
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Results */}
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">View Records</h5>
            {analytics && (
              <Badge bg="secondary">{rows.length} row{rows.length !== 1 ? 's' : ''}</Badge>
            )}
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
              <p className="mt-2 text-muted">Loading analytics...</p>
            </div>
          ) : rows.length === 0 ? (
            <Alert variant="info" className="mb-0">
              No product views found for the selected filters and date range.
            </Alert>
          ) : (
            <Table responsive bordered hover size="sm">
              <thead className="table-dark">
                <tr>
                  <th>Date</th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Product</th>
                  <th className="text-end">Views</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td>{row.viewed_at}</td>
                    <td>{row.user_name || <span className="text-muted">Anonymous</span>}</td>
                    <td>{row.user_email || <span className="text-muted">—</span>}</td>
                    <td>{row.product_name}</td>
                    <td className="text-end fw-semibold">{row.view_count}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ProductViewsDashboard;
